import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // If arriving from Emergent Google auth callback, let AuthCallback handle the exchange first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip the session probe on public auth routes — no session is expected there
    // and a 401 shouldn't produce noisy console errors during the reset-password flow.
    const publicPaths = ["/login", "/register", "/forgot-password", "/reset-password"];
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (publicPaths.some((p) => path === p || path.startsWith(p + "/"))) {
      setLoading(false);
      return;
    }
    let mounted = true;
    api.get("/auth/me")
      .then((r) => { if (mounted) setUser(r.data); })
      .catch(() => { /* unauthenticated — leave user null */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data.user);
    return data.user;
  }, []);

  const exchangeEmergentSession = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/emergent-session", { session_id });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* server already forgot us */ }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, register, exchangeEmergentSession, logout, loading }),
    [user, login, register, exchangeEmergentSession, logout, loading]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
